import React, { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { formatUtcToTz } from '../../utils/timezone';
import { Card, BlockStack, InlineStack, Text, Button, Spinner, Badge } from '@shopify/polaris';
import { PlayIcon } from '@shopify/polaris-icons';
import { logsApi } from '../../services/api';
import { formatServiceLabel } from '../../utils/serviceLabels';

export function SyncSummary({ 
  service, 
  onStart, 
  onStop, 
  onRefresh, 
  isLoading: externalLoading, 
  error: externalError,
  statusProp,
  lastSyncProp,
  progressProp
}) {
  // Internal state for summary log data
  const [status, setStatus] = useState('idle');
  const [lastSync, setLastSync] = useState(null);
  const [progress, setProgress] = useState('0 of 0');
  const [executionId, setExecutionId] = useState(null);
  const [internalError, setInternalError] = useState(null);
  const { currentWorkspace } = useWorkspace();
  const tz = currentWorkspace?.config?.timezone || 'America/New_York';
  // Remove enabled state - no longer needed in SyncSummary
  const selfPoll = statusProp == null; // if parent provides status, we don't poll here

  // Reset summary state immediately when switching services to prevent cross-tab bleed
  useEffect(() => {
    setStatus('idle');
    setLastSync(null);
    setProgress('0 of 0');
    setExecutionId(null);
    setInternalError(null);
  }, [service]);

  // Check summary logs for current sync status
  const checkSummaryLogs = useCallback(async () => {
    try {
      const data = await logsApi.getSummaryLogs(service);
      
      if (data.summaryLog) {
        // Map summary log status to internal status
        let newStatus = 'idle';
        const s = (data.summaryLog.status || '').toLowerCase();
        if (s === 'starting') newStatus = 'starting';
        else if (s === 'running') newStatus = 'running';
        else if (s === 'success') newStatus = 'success';
        else if (s === 'error') newStatus = 'error';
        else if (s === 'stopped') newStatus = 'stopped';
        
        setStatus(newStatus);
        const lastTs = data.summaryLog.updatedAt || data.summaryLog.createdAt || data.summaryLog.formattedTimestamp;
        setLastSync(lastTs);
        setExecutionId(data.summaryLog.executionId);
        setProgress(data.summaryLog.progress || '0 of 0');
        setInternalError(data.summaryLog.status === 'Error' ? data.summaryLog.message : null);
      } else {
        // No summary log found, set to idle
        setStatus('idle');
        setLastSync(null);
        setExecutionId(null);
        setProgress('0 of 0');
        setInternalError(null);
      }
    } catch (err) {
      setInternalError(err.message);
    }
  }, [service]);

  // Check summary logs on mount and when service changes (always poll if self-polling)
  useEffect(() => {
    if (selfPoll) {
      checkSummaryLogs();
    }
  }, [service, checkSummaryLogs, selfPoll]);

  // Poll for status updates continuously (always poll if self-polling)
  useEffect(() => {
    if (!selfPoll) return;
    const interval = setInterval(checkSummaryLogs, 5000);
    return () => clearInterval(interval);
  }, [checkSummaryLogs, selfPoll]);

  // Use internal error if no external error
  const displayError = externalError || internalError;
  const effectiveStatus = statusProp || status;
  const isBusy = effectiveStatus === 'starting' || effectiveStatus === 'running';
  const serviceLabel = formatServiceLabel(service);


  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'critical';
      case 'running':
        return 'info';
      case 'stopped':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    if (externalLoading && (status === 'starting' || status === 'running')) return status === 'starting' ? 'Starting' : 'Running';
    if (status === 'starting') return 'Starting';
    if (status === 'running') return 'Running';
    switch (status) {
      case 'success':
        return 'Success';
      case 'error':
        return 'Error';
      case 'stopped':
        return 'Stopped';
      case 'idle':
        return 'Idle';
      default:
        return status;
    }
  };

  return (
    <Card>
      <BlockStack gap="loose">
        <div className="card-title-container">
          <Text variant="headingMd" fontWeight="bold" className="card-title-standard">
            Sync Summary - {serviceLabel}
          </Text>
        </div>

        {displayError && (
          <Text variant="bodyMd" color="critical">
            Error: {displayError}
          </Text>
        )}

        <div className="sync-summary-grid grid-layout grid-3-columns">
          <div className="sync-summary-item status-item">
            <Text variant="bodySm" color="subdued" className="status-label">Status</Text>
            <div className="status-indicator">
              {isBusy && <Spinner size="small" />}
              <Badge tone={getStatusColor(effectiveStatus)}>
                {getStatusText(effectiveStatus)}
              </Badge>
            </div>
          </div>

          <div className="sync-summary-item status-item">
            <Text variant="bodySm" color="subdued" className="status-label">Last Sync</Text>
            <Text variant="bodyMd" className="status-value">
              {(lastSyncProp || lastSync) ? formatUtcToTz(lastSyncProp || lastSync, tz) : 'Never'}
            </Text>
          </div>

          <div className="sync-summary-item status-item">
            <Text variant="bodySm" color="subdued" className="status-label">Progress</Text>
            <Text variant="bodyMd" className="status-value">
              {effectiveStatus === 'starting' ? 'Starting' : (
                isBusy ? (progressProp || progress) : ((lastSyncProp || lastSync) ? (progressProp || progress) : 'Not running')
              )}
            </Text>
          </div>

          <div className="sync-summary-item action-item">
            <BlockStack gap="tight">
              <Text variant="bodySm" color="subdued" className="action-label">Actions</Text>
              <InlineStack gap="base" align="start" blockAlign="center">
                {onStart && (
                  <>
                    <Button
                      icon={PlayIcon}
                      primary
                      onClick={onStart}
                      disabled={externalLoading || isBusy}
                      loading={externalLoading || isBusy}
                    >
                      Start Sync
                    </Button>
                    <div style={{ width: '14px' }} />
                  </>
                )}
              </InlineStack>
            </BlockStack>
          </div>
        </div>
      </BlockStack>
    </Card>
  );
}
